'use client';

import React from 'react';
import { ApiResponse } from '@/types/use-case';

interface ApiResponseSnippetProps {
  response: ApiResponse;
  title?: string;
  showKeyFields?: string[];
}

export default function ApiResponseSnippet({ 
  response, 
  title = "API Response",
  showKeyFields 
}: ApiResponseSnippetProps) {
  // Format JSON with proper indentation
  const formattedResponse = JSON.stringify(response.data, null, 2);
  
  // Extract key fields for highlighting if specified
  const extractKeyFields = (data: any, fields: string[]): Record<string, any> => {
    const result: Record<string, any> = {};
    fields.forEach(field => {
      const value = getNestedValue(data, field);
      if (value !== undefined) {
        result[field] = value;
      }
    });
    return result;
  };

  const getNestedValue = (obj: any, path: string): any => {
    try {
      return path.split('.').reduce((current, key) => {
        if (current === null || current === undefined) {
          return undefined;
        }
        
        // Handle array indices
        if (Array.isArray(current) && !isNaN(Number(key))) {
          return current[Number(key)];
        }
        
        // Handle object properties
        if (current && typeof current === 'object' && key in current) {
          return current[key];
        }
        
        return undefined;
      }, obj);
    } catch (error) {
      console.warn(`Error accessing path "${path}":`, error);
      return undefined;
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold mb-4">{title}</h3>
      
      {/* Response Status */}
      <div className="flex items-center gap-4 mb-4">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          response.status >= 200 && response.status < 300 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          Status: {response.status}
        </span>
        {response.timestamp && (
          <span className="text-sm text-gray-600">
            {new Date(response.timestamp).toLocaleString()}
          </span>
        )}
      </div>

      {/* Key Fields Highlight */}
      {showKeyFields && showKeyFields.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-medium mb-3">Key Response Fields</h4>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {showKeyFields.map((field) => {
                const value = getNestedValue(response.data, field);
                const displayValue = value !== undefined && value !== null ? value : 'undefined';
                
                return (
                  <div key={field} className="bg-white p-3 rounded border">
                    <div className="text-sm font-medium text-gray-600 mb-1">
                      {field.split('.').pop()} {/* Show just the field name */}
                    </div>
                    <div className="text-xs text-gray-400 mb-1">
                      {field} {/* Show full path */}
                    </div>
                    <div 
                      className="text-lg text-gray-900"
                      style={{ 
                        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important'
                      }}
                    >
                      {value !== undefined && value !== null 
                        ? (typeof value === 'string' ? `"${value}"` : JSON.stringify(value))
                        : <span className="text-red-500 text-sm">Field not found</span>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Full Response */}
      <div>
        <h4 className="text-lg font-medium mb-3">Complete Response</h4>
        <div className="dark-code-block p-4 rounded-lg overflow-x-auto">
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            <code style={{ fontSize: '14px', lineHeight: '1.5' }}>{formattedResponse}</code>
          </pre>
        </div>
      </div>

      {/* Response Structure Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-lg font-medium mb-2">Response Structure</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <div>• <strong>Data Type:</strong> {Array.isArray(response.data) ? 'Array' : typeof response.data}</div>
          {response.data && typeof response.data === 'object' && !Array.isArray(response.data) && (
            <div>• <strong>Top-level Keys:</strong> {Object.keys(response.data).join(', ')}</div>
          )}
          <div>• <strong>Response Size:</strong> ~{Math.round(formattedResponse.length / 1024)}KB</div>
        </div>
      </div>
    </div>
  );
} 